import React, { useState } from "react"
import { ThemeProvider } from "styled-components"
import { subMinutes } from "date-fns"

import useFormTracking from "hooks/useFormTracking"
import isClientHook from "hooks/isClient"
import componentThemeExtension from "utils/componentThemeExtension"

import Webinar from "components/webinar"
import WebinarCountdown from "components/webinar-countdown"
import ContentfulSignupForm from "./sign-up-form"
import VideoWithChat from "./video-with-chat"

const ContentfulWebinar = ({ component, refs, idx, setWebinarAnnouncement }) => {
  const [countDownFinished, setCountDownFinished] = useState(false)
  const [triggerRender, setTriggerReRender] = useState(false)
  const isClient = isClientHook()

  if (!isClient) {
    return null
  }

  const params = new URLSearchParams(window.location.search)
  const email = params?.get("email")
  const emailFromSession = sessionStorage.getItem("webinarEmail")

  if (component.webinarVideo) {
    if (email || emailFromSession) {
      return (
        <VideoWithChat
          key={component.webinarVideo.id}
          component={component}
          index={idx}
          refs={refs}
        />
      )
    }

    return (
      <ContentfulSignupForm
        key={component.registrationSignupForm.id}
        component={{
          ...component.registrationSignupForm,
          eventDate: component.startDate,
          heading: component.heading,
          email: "",
          bodyText: {
            childMarkdownRemark: {
              html: "<p>This webinar has ended. Please sign up to watch the replay.</p>",
            },
          },
          onEmailSubscribe: (subscribedEmail) => {
            sessionStorage.setItem("webinarEmail", subscribedEmail)
            setTriggerReRender(true)
          },
        }}
        trackingId={component.trackingId}
        elRef={refs[idx]}
      />
    )
  }

  if (
    new Date() < subMinutes(new Date(component.startDate), 5) &&
    !countDownFinished &&
    (email || emailFromSession)
  ) {
    return (
      <WebinarCountdown
        heading={component.heading}
        startDate={component.startDate}
        onTickCallback={(targetDate) => {
          if (subMinutes(new Date(targetDate), 5) < new Date()) {
            setCountDownFinished(true)
          }
        }}
      />
    )
  }

  if (
    new Date() < subMinutes(new Date(component.startDate), 5) &&
    component.registrationSignupForm &&
    component.registrationSignupForm.id &&
    !email &&
    !emailFromSession
  ) {
    return (
      <ContentfulSignupForm
        key={component.registrationSignupForm.id}
        component={{
          ...component.registrationSignupForm,
          eventDate: component.startDate,
          heading: component.heading,
          onEmailSubscribe: (subscribedEmail) => {
            sessionStorage.setItem("webinarEmail", subscribedEmail)
            setTriggerReRender(true)
          },
        }}
        trackingId={component.trackingId}
        elRef={refs[idx]}
      />
    )
  }

  return (
    <WebinarWrapper
      key={component.id}
      component={{
        emailList: component.registrationSignupForm?.emailList,
        emailSource: component.registrationSignupForm?.emailSource,
        email: email || "",
        ...component,
      }}
      elRef={refs[idx]}
      setWebinarAnnouncement={setWebinarAnnouncement}
    />
  )
}

const WebinarWrapper = ({ component, nested, elRef, setWebinarAnnouncement }) => {
  const {
    trackingId,
    startDate,
    durationInMinutes,
    webinarId,
    promotions,
    heading,
    emailList,
    emailSource,
    email,
  } = component
  const { trackEmailFormView } = useFormTracking()

  trackEmailFormView(elRef, trackingId, { webinar_id: webinarId })

  return (
    <ThemeProvider theme={componentThemeExtension(null, nested)}>
      <Webinar
        roomCode={webinarId}
        setWebinarAnnouncement={setWebinarAnnouncement}
        promotions={promotions}
        startDate={startDate}
        title={heading}
        trackingId={trackingId}
        durationInMinutes={durationInMinutes}
        emailList={emailList}
        emailSource={emailSource}
        email={email}
      />
    </ThemeProvider>
  )
}

export default ContentfulWebinar
